/**
 * Reports — Automatisierte Geschäftsberichte mit Zeitraumauswahl.
 *
 * Features:
 * - Ampel-System (grün/gelb/rot) für Berichts-Status
 * - Zeitraum-Schnellauswahl (Gestern, letzte Woche, Monat, Quartal)
 * - Report-Generierung (z.B. DB-Qualitätsreport)
 * - PDF-Download und Detail-Ansicht
 * - Datenvollständigkeit: Aktualität + Lücken aller Datenquellen
 *
 * @page /reports
 * @roles admin, manager
 * @i18n reports, common
 * @endpoints GET /api/reports, GET /api/reports/:id, POST /api/reports/generate/db-qualitaetsreport, GET /api/reports/datenvollstaendigkeit
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Database, CheckCircle2, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function fmt(n, d = 0) {
  if (n == null) return '-';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });
}
function fmtDate(d) {
  if (!d) return '-';
  const dt = new Date(String(d).slice(0, 10) + 'T12:00:00');
  return `${dt.getDate().toString().padStart(2, '0')}.${(dt.getMonth() + 1).toString().padStart(2, '0')}.${String(dt.getFullYear()).slice(2)}`;
}
function toISO(d) {
  return d instanceof Date ? d.toISOString().slice(0, 10) : d;
}

function AmpelDot({ ampel, size = 'w-3 h-3' }) {
  const color = ampel === 'gruen' ? '#34d399' : ampel === 'gelb' ? '#fbbf24' : '#ef4444';
  return <span className={`inline-block ${size} rounded-full`} style={{ backgroundColor: color }} />;
}

// Zeitraum display helper
function fmtZeitraum(von, bis) {
  if (!von || !bis) return fmtDate(von || bis);
  if (von === bis) return fmtDate(von);
  return `${fmtDate(von)} – ${fmtDate(bis)}`;
}

// Schnellauswahl presets
function getPresets(t) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // This week: Monday to today
  const dayOfWeek = today.getDay() || 7; // 0=Sun → 7
  const monday = new Date(today);
  monday.setDate(today.getDate() - dayOfWeek + 1);

  // Last week: Mon-Sun
  const lastWeekMon = new Date(monday);
  lastWeekMon.setDate(monday.getDate() - 7);
  const lastWeekSun = new Date(lastWeekMon);
  lastWeekSun.setDate(lastWeekMon.getDate() + 6);

  // This month: 1st to today
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  // Last month
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const lastMonthStart = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);

  // Last quarter
  const currentQ = Math.floor(today.getMonth() / 3);
  const lastQStart = new Date(today.getFullYear(), (currentQ - 1) * 3, 1);
  const lastQEnd = new Date(today.getFullYear(), currentQ * 3, 0);
  // If Q1 (currentQ=0), go to previous year Q4
  if (currentQ === 0) {
    lastQStart.setFullYear(today.getFullYear() - 1);
    lastQStart.setMonth(9); // October
    lastQEnd.setFullYear(today.getFullYear() - 1);
    lastQEnd.setMonth(11);
    lastQEnd.setDate(31);
  }

  return [
    { label: t('reports.yesterday'), von: toISO(yesterday), bis: toISO(yesterday) },
    { label: t('reports.lastWeek'), von: toISO(lastWeekMon), bis: toISO(lastWeekSun) },
    { label: t('reports.lastMonth'), von: toISO(lastMonthStart), bis: toISO(lastMonthEnd) },
    { label: t('reports.thisMonth'), von: toISO(thisMonthStart), bis: toISO(today) },
    { label: t('reports.lastQuarter'), von: toISO(lastQStart), bis: toISO(lastQEnd) },
  ];
}

/* ─── Generate Modal ─────────────────────────────── */
function GenerateModal({ onClose, onGenerate, generating, t }) {
  const presets = getPresets(t);
  const [von, setVon] = useState(presets[0].von);
  const [bis, setBis] = useState(presets[0].bis);
  const [activePreset, setActivePreset] = useState(presets[0].label);
  const backdropRef = useRef(null);

  const selectPreset = (p) => {
    setVon(p.von);
    setBis(p.bis);
    setActivePreset(p.label);
  };

  const handleCustom = () => {
    setActivePreset(null);
  };

  return (
    <div ref={backdropRef} onClick={e => e.target === backdropRef.current && onClose()}
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-surface-card border border-line rounded-card shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h3 className="text-lg font-bold text-content">{t('reports.generateReport')}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-hover text-[var(--text-tertiary)] hover:text-content">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Schnellauswahl */}
        <div className="px-5 pt-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('reports.quickSelect')}</div>
          <div className="flex flex-wrap gap-2">
            {presets.map(p => (
              <button key={p.label} onClick={() => selectPreset(p)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  activePreset === p.label
                    ? 'bg-purple-500/20 text-purple-400 border-purple-500/40 font-medium'
                    : 'bg-surface-inset text-[var(--text-secondary)] border-line hover:bg-surface-hover hover:text-content'
                }`}>
                {p.label}
              </button>
            ))}
            <button onClick={handleCustom}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                activePreset === null
                  ? 'bg-purple-500/20 text-purple-400 border-purple-500/40 font-medium'
                  : 'bg-surface-inset text-[var(--text-secondary)] border-line hover:bg-surface-hover hover:text-content'
              }`}>
              {t('reports.custom')}
            </button>
          </div>
        </div>

        {/* Date inputs */}
        <div className="px-5 pt-4 pb-2">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('reports.period')}</div>
          <div className="flex gap-3 items-center">
            <div className="flex-1">
              <label className="text-[10px] text-[var(--text-tertiary)] mb-1 block">{t('common.from')}</label>
              <input type="date" value={von} onChange={e => { setVon(e.target.value); setActivePreset(null); }}
                className="w-full px-3 py-2 text-sm bg-surface-inset border border-line rounded-lg text-content focus:outline-none focus:border-purple-500/50" />
            </div>
            <span className="text-[var(--text-tertiary)] mt-4">–</span>
            <div className="flex-1">
              <label className="text-[10px] text-[var(--text-tertiary)] mb-1 block">{t('common.to')}</label>
              <input type="date" value={bis} onChange={e => { setBis(e.target.value); setActivePreset(null); }}
                className="w-full px-3 py-2 text-sm bg-surface-inset border border-line rounded-lg text-content focus:outline-none focus:border-purple-500/50" />
            </div>
          </div>
          {von && bis && (
            <div className="mt-2 text-xs text-[var(--text-secondary)]">
              {t('reports.period')}: <span className="font-medium text-content-secondary">{fmtZeitraum(von, bis)}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t border-line mt-2 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-content rounded-lg hover:bg-surface-hover transition-colors">
            {t('common.cancel')}
          </button>
          <button onClick={() => onGenerate(von, bis)} disabled={generating || !von || !bis}
            className="px-4 py-2 bg-purple-500/15 text-purple-400 border border-purple-500/30 rounded-lg text-sm font-medium hover:bg-purple-500/25 transition-colors disabled:opacity-50 flex items-center gap-2">
            {generating ? (
              <><div className="animate-spin h-4 w-4 border-2 border-purple-400 border-t-transparent rounded-full" /> {t('reports.generating')}</>
            ) : (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg> {t('reports.generate')}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Report Detail View ─────────────────────────────── */
function ReportDetail({ report, onBack, t }) {
  const daten = report.daten || {};
  const kunden = daten.kunden || [];
  const ampelCounts = daten.ampel_counts || {};
  const zeitraum = fmtZeitraum(report.von || daten.von, report.bis || daten.bis);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-surface-hover text-[var(--text-tertiary)] hover:text-content transition-colors">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
        </button>
        <div>
          <h2 className="text-xl font-extrabold text-content flex items-center gap-2">
            <AmpelDot ampel={report.ampel} size="w-4 h-4" />
            {t('reports.qualityReport')} {zeitraum}
          </h2>
          <p className="text-xs text-[var(--text-secondary)] mt-1">{report.zusammenfassung}</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('reports.orders')}</div>
          <div className="text-2xl font-mono font-bold text-content">{fmt(daten.auftraege)}</div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">Billing %</div>
          <div className="text-2xl font-mono font-bold text-content">{fmt(daten.avg_billing_pct, 1)}%</div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('reports.green')}</div>
          <div className="text-2xl font-mono font-bold text-content">{ampelCounts.gruen || 0}</div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('reports.yellow')}</div>
          <div className="text-2xl font-mono font-bold text-content">{ampelCounts.gelb || 0}</div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('reports.red')}</div>
          <div className="text-2xl font-mono font-bold text-content">{ampelCounts.rot || 0}</div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">Orphans</div>
          <div className="text-2xl font-mono font-bold text-content">{fmt(daten.orphan_pickings)}</div>
        </div>
      </div>

      {/* Kunden table */}
      <div className="bg-surface-card border border-line rounded-card">
        <div className="px-5 py-3 border-b border-line">
          <span className="text-sm font-bold text-content">{t('reports.customerQuality')}</span>
          <span className="ml-2 text-[10px] bg-surface-inset text-[var(--text-secondary)] px-1.5 py-0.5 rounded-full font-mono">{kunden.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                <th className="py-3 pl-4 text-center w-10">{t('reports.trafficLight')}</th>
                <th className="py-3 text-left">{t('common.customer')}</th>
                <th className="py-3 text-left">{t('common.segment')}</th>
                <th className="py-3 text-right">{t('reports.orders')}</th>
                <th className="py-3 text-right">Billing%</th>
                <th className="py-3 text-right">Pick%</th>
                <th className="py-3 text-right">Pack%</th>
                <th className="py-3 text-left pl-4">{t('reports.warnings')}</th>
              </tr>
            </thead>
            <tbody>
              {kunden.map(k => (
                <tr key={k.kunde_id} className="border-b border-line hover:bg-surface-hover transition-colors">
                  <td className="py-2.5 pl-4 text-center"><AmpelDot ampel={k.ampel} /></td>
                  <td className="py-2.5 text-xs font-medium text-content">{k.name}</td>
                  <td className="py-2.5 text-xs text-[var(--text-secondary)]">
                    {k.segment && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                        k.segment === 'A' ? 'bg-primary-500/15 text-primary-400' :
                        k.segment === 'B' ? 'bg-purple-500/15 text-purple-400' : 'bg-amber-500/15 text-amber-400'
                      }`}>{k.segment}</span>
                    )}
                  </td>
                  <td className="py-2.5 text-right font-mono text-xs text-content-secondary">{fmt(k.auftraege)}</td>
                  <td className="py-2.5 text-right font-mono text-xs">
                    <span className="inline-flex items-center justify-end gap-1.5 text-[var(--text-primary)]">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: k.billing_pct >= 95 ? '#34d399' : k.billing_pct >= 80 ? '#fbbf24' : '#ef4444' }} />
                      {fmt(k.billing_pct, 1)}%
                    </span>
                  </td>
                  <td className="py-2.5 text-right font-mono text-xs">
                    <span className="inline-flex items-center justify-end gap-1.5 text-[var(--text-primary)]">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: k.pick_pct >= 80 ? '#34d399' : k.pick_pct >= 60 ? '#fbbf24' : '#ef4444' }} />
                      {fmt(k.pick_pct, 1)}%
                    </span>
                  </td>
                  <td className="py-2.5 text-right font-mono text-xs">
                    <span className="inline-flex items-center justify-end gap-1.5 text-[var(--text-primary)]">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: k.pack_pct >= 80 ? '#34d399' : k.pack_pct >= 60 ? '#fbbf24' : '#ef4444' }} />
                      {fmt(k.pack_pct, 1)}%
                    </span>
                  </td>
                  <td className="py-2.5 pl-4 text-xs text-yellow-400">
                    {(k.warnungen || []).join(', ') || '-'}
                  </td>
                </tr>
              ))}
              {kunden.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-[var(--text-tertiary)] text-sm">{t('reports.noCustomerData')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Datenvollständigkeit Tab
   ═══════════════════════════════════════════════════════════ */

function formatAlter(min, t) {
  if (min == null) return '—';
  if (min < 60) return `${min} min`;
  if (min < 1440) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}h ${m}min`;
  }
  const d = Math.floor(min / 1440);
  return `${d} ${t('common.days') || 'days'}`;
}

function StatusBadge({ status, t }) {
  const cfg = {
    ok: { label: t('reports.dv.statusOk'), cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', Icon: CheckCircle2 },
    warning: { label: t('reports.dv.statusWarning'), cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', Icon: AlertTriangle },
    error: { label: t('reports.dv.statusError'), cls: 'bg-red-500/10 text-red-600 dark:text-red-400', Icon: XCircle },
  };
  const c = cfg[status] || cfg.error;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-badge text-[11px] font-medium whitespace-nowrap ${c.cls}`}>
      <c.Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

function LueckenHeatmap({ tage, t }) {
  if (!tage || tage.length === 0) {
    return <span className="text-[10px] text-[var(--text-tertiary)]">{t('reports.dv.keinTagesTracking')}</span>;
  }
  return (
    <div className="flex gap-[2px] flex-wrap">
      {tage.map(tag => {
        const d = typeof tag.datum === 'string' ? tag.datum : new Date(tag.datum).toISOString().slice(0, 10);
        const hasData = tag.eintraege > 0;
        // Check if weekend
        const dt = new Date(d + 'T12:00:00');
        const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
        const bg = hasData ? '#10B981' : isWeekend ? '#D1D5DB' : '#EF4444';
        return (
          <div
            key={d}
            title={`${d}: ${fmt(tag.eintraege)}`}
            className="w-[14px] h-[14px] rounded-[2px] transition-opacity hover:opacity-80"
            style={{ backgroundColor: bg }}
          />
        );
      })}
    </div>
  );
}

function DatenvollstaendigkeitTab() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/reports/datenvollstaendigkeit`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-card text-sm text-red-500">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const quellen = data.quellen || [];
  const syncLog = data.sync_log || [];
  const counts = { ok: 0, warning: 0, error: 0 };
  quellen.forEach(q => { counts[q.status] = (counts[q.status] || 0) + 1; });

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{t('reports.dv.statusOk')}</div>
          <div className="text-2xl font-mono font-bold text-content">{counts.ok}</div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{t('reports.dv.statusWarning')}</div>
          <div className="text-2xl font-mono font-bold text-content">{counts.warning}</div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{t('reports.dv.statusError')}</div>
          <div className="text-2xl font-mono font-bold text-content">{counts.error}</div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-4">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{t('reports.dv.generiert')}</div>
          <div className="text-sm font-mono text-content mt-1">
            {data.generiert_am ? new Date(data.generiert_am).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' }) : '—'}
          </div>
          <button onClick={load} className="mt-1 text-[10px] text-primary-500 hover:text-primary-400 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Quellen Table */}
      <div className="bg-surface-card border border-line rounded-card">
        <div className="px-5 py-3 border-b border-line">
          <span className="text-sm font-bold text-content">{t('reports.dv.quellen')}</span>
          <span className="ml-2 text-[10px] bg-surface-inset text-[var(--text-secondary)] px-1.5 py-0.5 rounded-full font-mono">{quellen.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                <th className="py-3 pl-5 text-left">{t('reports.dv.quellen')}</th>
                <th className="py-3 text-left">{t('reports.dv.system')}</th>
                <th className="py-3 text-right">{t('reports.dv.eintraege30d')}</th>
                <th className="py-3 text-left pl-4">{t('reports.dv.letzterEintrag')}</th>
                <th className="py-3 text-left">{t('reports.dv.luecken')}</th>
                <th className="py-3 text-center pr-5">{t('reports.dv.status')}</th>
              </tr>
            </thead>
            <tbody>
              {quellen.map(q => (
                <tr key={q.id} className="border-b border-line last:border-b-0 hover:bg-surface-hover transition-colors">
                  <td className="py-3 pl-5">
                    <div className="font-medium text-content text-xs">{q.name}</div>
                  </td>
                  <td className="py-3 text-xs text-[var(--text-secondary)]">{q.system}</td>
                  <td className="py-3 text-right font-mono text-xs text-content">{fmt(q.gesamt_30d)}</td>
                  <td className="py-3 pl-4">
                    <div className="text-xs text-content">
                      {formatAlter(q.alter_minuten, t)}
                      {q.anzeige_typ === 'sync_log' && q.records_synced != null && (
                        <span className="text-[10px] text-[var(--text-tertiary)] ml-1">({q.records_synced} records)</span>
                      )}
                    </div>
                    {q.anzeige_typ === 'sync_log' ? (
                      q.letzter_sync && (
                        <div className="text-[10px] text-[var(--text-tertiary)]">
                          {t('reports.dv.lastSync')}: {new Date(q.letzter_sync).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )
                    ) : (
                      q.letzter_eintrag && (
                        <div className="text-[10px] text-[var(--text-tertiary)]">
                          {new Date(q.letzter_eintrag).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )
                    )}
                  </td>
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <LueckenHeatmap tage={q.tage_30d} t={t} />
                      {q.luecken_anzahl != null && q.tage_30d.length > 0 && (
                        <span className={`text-[10px] font-mono whitespace-nowrap ${q.luecken_anzahl > 5 ? 'text-red-500' : q.luecken_anzahl > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                          {q.luecken_anzahl}d
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 text-center pr-5"><StatusBadge status={q.status} t={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sync Log */}
      {syncLog.length > 0 && (
        <div className="bg-surface-card border border-line rounded-card">
          <div className="px-5 py-3 border-b border-line">
            <span className="text-sm font-bold text-content">{t('reports.dv.syncLog')}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                  <th className="py-2 pl-5 text-left">Source</th>
                  <th className="py-2 text-left">Job</th>
                  <th className="py-2 text-left">{t('reports.dv.status')}</th>
                  <th className="py-2 text-right">Fetched</th>
                  <th className="py-2 text-right">Created</th>
                  <th className="py-2 text-right pr-5">{t('reports.dv.letzterEintrag')}</th>
                </tr>
              </thead>
              <tbody>
                {syncLog.map((s, i) => (
                  <tr key={i} className="border-b border-line last:border-b-0 hover:bg-surface-hover transition-colors">
                    <td className="py-2 pl-5 text-content font-medium">{s.source}</td>
                    <td className="py-2 text-[var(--text-secondary)]">{s.job}</td>
                    <td className="py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        s.status === 'success' ? 'bg-emerald-500/10 text-emerald-500'
                        : s.status === 'error' ? 'bg-red-500/10 text-red-500'
                        : 'bg-amber-500/10 text-amber-500'
                      }`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="py-2 text-right font-mono text-content">{s.entries_fetched || 0}</td>
                    <td className="py-2 text-right font-mono text-content">{s.entries_created || 0}</td>
                    <td className="py-2 text-right pr-5 text-[var(--text-secondary)]">
                      {s.zeit ? new Date(s.zeit).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] text-[var(--text-tertiary)]">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: '#10B981' }} /> Daten vorhanden</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: '#EF4444' }} /> Lücke (Werktag)</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: '#D1D5DB' }} /> Wochenende</div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main Reports Component
   ═══════════════════════════════════════════════════════════ */
export default function Reports() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useTabParam('quality');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  const loadReports = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/reports?limit=50`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => { setReports(d.reports || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  const generateReport = useCallback(async (von, bis) => {
    setGenerating(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/reports/generate/db-qualitaetsreport`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ von, bis }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (data.ok) {
        setShowModal(false);
        loadReports();
        setSelectedReport(data.report);
      } else if (data.error) {
        setError(data.error);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  }, [loadReports]);

  const openReport = useCallback(async (id) => {
    try {
      const r = await fetch(`${API}/api/reports/${id}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const report = await r.json();
      setSelectedReport(report);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const tabs = [
    { key: 'quality', label: t('reports.tabQuality') },
    { key: 'dv', label: t('reports.dv.title'), Icon: Database },
  ];

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-content">{t('reports.title')}</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">{t('reports.subtitle')}</p>
        </div>
        {activeTab === 'quality' && !selectedReport && (
          <button onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-purple-500/15 text-purple-400 border border-purple-500/30 rounded-lg text-sm font-medium hover:bg-purple-500/25 transition-colors flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
            {t('reports.newReport')}
          </button>
        )}
      </div>

      {/* Tabs */}
      {!selectedReport && (
        <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] gap-1 border border-[var(--border-default,#E5E7EB)] mb-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-[8px] text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              {tab.Icon && <tab.Icon className="w-4 h-4" />}
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-card text-sm text-red-400 flex items-center justify-between">
          <span>{t('common.error')}: {error}</span>
          <button onClick={() => setError(null)} className="text-red-400/60 hover:text-red-400">&times;</button>
        </div>
      )}

      {/* Generate Modal */}
      {showModal && (
        <GenerateModal
          onClose={() => setShowModal(false)}
          onGenerate={generateReport}
          generating={generating}
          t={t}
        />
      )}

      {/* Datenvollständigkeit Tab */}
      {activeTab === 'dv' && !selectedReport && (
        <DatenvollstaendigkeitTab />
      )}

      {/* Quality Reports Tab */}
      {activeTab === 'quality' && (
        <>
          {/* Detail view */}
          {selectedReport && (
            <ReportDetail report={selectedReport} onBack={() => setSelectedReport(null)} t={t} />
          )}

          {/* List view */}
          {!selectedReport && (
            <>
              {loading && (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin h-6 w-6 border-2 border-purple-400 border-t-transparent rounded-full" />
                  <span className="ml-3 text-sm text-[var(--text-secondary)]">{t('reports.loading')}</span>
                </div>
              )}

              {!loading && reports.length === 0 && (
                <div className="flex flex-col items-center gap-3 p-8 bg-surface-card border border-line rounded-card text-center">
                  <svg className="w-12 h-12 text-[var(--text-tertiary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <div className="text-base font-medium text-content mb-1">{t('reports.noReports')}</div>
                    <div className="text-sm text-[var(--text-secondary)] mb-4">{t('reports.noReportsDesc')}</div>
                  </div>
                </div>
              )}

              {!loading && reports.length > 0 && (
                <div className="bg-surface-card border border-line rounded-card">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                          <th className="py-3 pl-4 text-left">{t('reports.period')}</th>
                          <th className="py-3 text-center w-10">{t('reports.trafficLight')}</th>
                          <th className="py-3 text-right">{t('reports.orders')}</th>
                          <th className="py-3 text-right">Billing%</th>
                          <th className="py-3 text-center">{t('reports.green')}</th>
                          <th className="py-3 text-center">{t('reports.yellow')}</th>
                          <th className="py-3 text-center">{t('reports.red')}</th>
                          <th className="py-3 text-right">{t('reports.warnings')}</th>
                          <th className="py-3 pr-4 text-right w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports.map(r => {
                          const counts = r.ampel_counts || {};
                          return (
                            <tr key={r.id} className="border-b border-line hover:bg-surface-hover transition-colors">
                              <td className="py-3 pl-4 text-xs font-medium text-content">{fmtZeitraum(r.von, r.bis)}</td>
                              <td className="py-3 text-center"><AmpelDot ampel={r.ampel} /></td>
                              <td className="py-3 text-right font-mono text-xs text-content-secondary">{fmt(r.auftraege)}</td>
                              <td className="py-3 text-right font-mono text-xs text-content-secondary">{fmt(r.avg_billing_pct, 1)}%</td>
                              <td className="py-3 text-center font-mono text-xs">
                                <span className="inline-flex items-center justify-center gap-1.5 text-[var(--text-primary)]">
                                  <span className="w-2 h-2 rounded-full shrink-0 bg-emerald-400" />
                                  {counts.gruen || 0}
                                </span>
                              </td>
                              <td className="py-3 text-center font-mono text-xs">
                                <span className="inline-flex items-center justify-center gap-1.5 text-[var(--text-primary)]">
                                  <span className="w-2 h-2 rounded-full shrink-0 bg-yellow-400" />
                                  {counts.gelb || 0}
                                </span>
                              </td>
                              <td className="py-3 text-center font-mono text-xs">
                                <span className="inline-flex items-center justify-center gap-1.5 text-[var(--text-primary)]">
                                  <span className="w-2 h-2 rounded-full shrink-0 bg-red-400" />
                                  {counts.rot || 0}
                                </span>
                              </td>
                              <td className="py-3 text-right font-mono text-xs text-[var(--text-secondary)]">{r.warnungen || 0}</td>
                              <td className="py-3 pr-4 text-right">
                                <button onClick={() => openReport(r.id)}
                                  className="px-2.5 py-1 text-[10px] bg-purple-500/15 text-purple-400 border border-purple-500/30 rounded-lg hover:bg-purple-500/25 transition-colors">
                                  {t('common.open')}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
